// ---------------------------------------------------------------------------
// Navigation Registry — static list of navigable pages & quick actions
// Used by Command Palette for "/" prefix (navigate) and ">" prefix (actions)
// ---------------------------------------------------------------------------

export interface NavigationItem {
  label: string;
  path: string;
  icon: string; // Lucide icon name
  module: string;
  keywords?: string[]; // Extra search terms
}

export interface QuickAction {
  label: string;
  path: string;
  icon: string;
  keywords?: string[];
}

// ---------------------------------------------------------------------------
// Pages — "/" prefix mode
// ---------------------------------------------------------------------------

export const navigationItems: NavigationItem[] = [
  // Car Rental
  {
    label: "แดชบอร์ดรถเช่า",
    path: "/car-rental",
    icon: "Car",
    module: "car-rental",
    keywords: ["dashboard", "rental"],
  },
  {
    label: "การจอง",
    path: "/car-rental/bookings",
    icon: "CalendarDays",
    module: "car-rental",
    keywords: ["booking", "จอง"],
  },
  {
    label: "รถให้เช่า",
    path: "/car-rental/vehicles",
    icon: "Car",
    module: "car-rental",
    keywords: ["vehicle", "รถ"],
  },
  {
    label: "สาขา/จุดรับรถ",
    path: "/car-rental/locations",
    icon: "MapPin",
    module: "car-rental",
    keywords: ["location", "สาขา"],
  },

  // Accounting
  {
    label: "ใบแจ้งหนี้ขาย",
    path: "/accounting/invoices?type=sales",
    icon: "FileText",
    module: "accounting",
    keywords: ["sales invoice", "invoice", "บิล", "ขาย"],
  },
  {
    label: "ใบแจ้งหนี้ซื้อ",
    path: "/accounting/invoices?type=purchase",
    icon: "FileText",
    module: "accounting",
    keywords: ["purchase invoice", "invoice", "บิล", "ซื้อ"],
  },
  {
    label: "ใบลดหนี้ขาย",
    path: "/accounting/credit-notes?partyType=customer",
    icon: "FileMinus",
    module: "accounting",
    keywords: ["sales credit note", "credit note", "cn", "ใบลดหนี้", "ขาย"],
  },
  {
    label: "ใบลดหนี้ซื้อ",
    path: "/accounting/credit-notes?partyType=supplier",
    icon: "FileMinus",
    module: "accounting",
    keywords: ["purchase credit note", "credit note", "cn", "ใบลดหนี้", "ซื้อ"],
  },
  {
    label: "ใบเพิ่มหนี้",
    path: "/accounting/debit-notes",
    icon: "FilePlus",
    module: "accounting",
    keywords: ["debit note", "dn", "ใบเพิ่มหนี้"],
  },
  {
    label: "การชำระเงิน",
    path: "/accounting/payments",
    icon: "CreditCard",
    module: "accounting",
    keywords: ["payment", "จ่าย"],
  },
  {
    label: "ลูกค้า",
    path: "/accounting/customers",
    icon: "Users",
    module: "accounting",
    keywords: ["customer", "ลูกค้า"],
  },
  {
    label: "ผู้ขาย",
    path: "/accounting/suppliers",
    icon: "Handshake",
    module: "accounting",
    keywords: ["supplier", "vendor", "ผู้ขาย", "ผู้จำหน่าย"],
  },
  {
    label: "ผังบัญชี",
    path: "/accounting/chart-of-accounts",
    icon: "Calculator",
    module: "accounting",
    keywords: ["account", "chart", "บัญชี"],
  },
  {
    label: "รายการบันทึกบัญชี",
    path: "/accounting/journal-entries",
    icon: "BookOpen",
    module: "accounting",
    keywords: ["journal", "entry"],
  },

  // Fleet
  {
    label: "รถทั้งหมด",
    path: "/fleet/vehicles",
    icon: "Car",
    module: "fleet",
    keywords: ["vehicle", "fleet", "รถ"],
  },
  {
    label: "การซ่อมบำรุง",
    path: "/fleet/maintenance",
    icon: "Wrench",
    module: "fleet",
    keywords: ["maintenance", "ซ่อม"],
  },

  // CRM
  {
    label: "Leads",
    path: "/crm/leads",
    icon: "UserPlus",
    module: "crm",
    keywords: ["lead", "ลูกค้าใหม่"],
  },
  {
    label: "Deals",
    path: "/crm/deals",
    icon: "Handshake",
    module: "crm",
    keywords: ["deal", "ดีล"],
  },
  {
    label: "ผู้ติดต่อ",
    path: "/crm/contacts",
    icon: "Contact",
    module: "crm",
    keywords: ["contact", "ติดต่อ"],
  },
  {
    label: "องค์กร",
    path: "/crm/organizations",
    icon: "Building2",
    module: "crm",
    keywords: ["organization", "บริษัท"],
  },

  // Settings
  {
    label: "ตั้งค่า",
    path: "/settings/dashboard",
    icon: "Settings",
    module: "core",
    keywords: ["settings", "ตั้งค่า"],
  },
  {
    label: "ผู้ใช้",
    path: "/settings/users",
    icon: "Users",
    module: "core",
    keywords: ["user", "ผู้ใช้"],
  },
  {
    label: "สิทธิ์และบทบาท",
    path: "/settings/roles",
    icon: "Shield",
    module: "core",
    keywords: ["role", "permission", "สิทธิ์"],
  },
];

// ---------------------------------------------------------------------------
// Quick Actions — ">" prefix mode
// ---------------------------------------------------------------------------

export const quickActions: QuickAction[] = [
  {
    label: "สร้างการจองใหม่",
    path: "/car-rental/bookings?action=new",
    icon: "Plus",
    keywords: ["create", "booking", "สร้าง", "จอง"],
  },
  {
    label: "สร้างใบแจ้งหนี้ขาย",
    path: "/accounting/invoices/new",
    icon: "Plus",
    keywords: ["create", "sales invoice", "invoice", "สร้าง", "บิล", "ขาย"],
  },
  {
    label: "สร้างใบแจ้งหนี้ซื้อ",
    path: "/accounting/invoices/new?type=purchase",
    icon: "Plus",
    keywords: ["create", "purchase invoice", "invoice", "สร้าง", "บิล", "ซื้อ"],
  },
  {
    label: "สร้างใบลดหนี้ขาย",
    path: "/accounting/credit-notes/new?partyType=customer",
    icon: "Plus",
    keywords: ["create", "sales credit note", "credit note", "cn", "สร้าง", "ใบลดหนี้", "ขาย"],
  },
  {
    label: "สร้างใบลดหนี้ซื้อ",
    path: "/accounting/credit-notes/new?partyType=supplier",
    icon: "Plus",
    keywords: ["create", "purchase credit note", "credit note", "cn", "สร้าง", "ใบลดหนี้", "ซื้อ"],
  },
  {
    label: "สร้างใบเพิ่มหนี้",
    path: "/accounting/debit-notes/new?partyType=supplier",
    icon: "Plus",
    keywords: ["create", "debit note", "dn", "สร้าง", "ใบเพิ่มหนี้"],
  },
  {
    label: "สร้าง Lead ใหม่",
    path: "/crm/leads?action=new",
    icon: "Plus",
    keywords: ["create", "lead", "สร้าง"],
  },
  {
    label: "สร้าง Deal ใหม่",
    path: "/crm/deals?action=new",
    icon: "Plus",
    keywords: ["create", "deal", "สร้าง", "ดีล"],
  },
  {
    label: "เพิ่มลูกค้าใหม่",
    path: "/accounting/customers?action=new",
    icon: "UserPlus",
    keywords: ["create", "customer", "สร้าง", "ลูกค้า"],
  },
  {
    label: "เพิ่มผู้ขายใหม่",
    path: "/accounting/suppliers?action=new",
    icon: "Handshake",
    keywords: ["create", "supplier", "vendor", "สร้าง", "ผู้ขาย", "ผู้จำหน่าย"],
  },
  {
    label: "เพิ่มรถใหม่",
    path: "/fleet/vehicles?action=new",
    icon: "Plus",
    keywords: ["create", "vehicle", "สร้าง", "รถ"],
  },
];

// ---------------------------------------------------------------------------
// Search helpers
// ---------------------------------------------------------------------------

/**
 * Filter navigation items by query (checks label, path, keywords).
 */
export function filterNavigationItems(
  items: NavigationItem[],
  query: string,
): NavigationItem[] {
  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.path.toLowerCase().includes(q) ||
      item.keywords?.some((kw) => kw.toLowerCase().includes(q)),
  );
}

/**
 * Filter quick actions by query.
 */
export function filterQuickActions(
  actions: QuickAction[],
  query: string,
): QuickAction[] {
  const q = query.toLowerCase();
  return actions.filter(
    (action) =>
      action.label.toLowerCase().includes(q) ||
      action.keywords?.some((kw) => kw.toLowerCase().includes(q)),
  );
}
